import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const TODAY = new Date().toISOString().split('T')[0];
const EXPENSE_TYPES = ['Van','Bike','On Foot','Company Vehicle'];
const EMPTY = {
  dispatch_date: TODAY, from_unit_id:'', to_unit_id:'', sent_by_employee_id:'',
  handler_name:'', vehicle_id:'', expense_type:'Van', van_rent:0, fuel:0, misc_expense:0, remarks:''
};

export default function InternalCourier() {
  const { user } = useAuth();
  const [couriers, setCouriers] = useState([]);
  const [units, setUnits] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get('/api/internal-couriers'); setCouriers(r.data); }
    catch (err) { console.error('internal-couriers:', err); setCouriers([]); }
    try { const r = await api.get('/api/units'); setUnits(r.data); }
    catch (err) { console.error('units:', err); setUnits([]); }
    try { const r = await api.get('/api/vehicles'); setVehicles(r.data); }
    catch (err) { console.error('vehicles:', err); setVehicles([]); }
    try { const r = await api.get('/api/employees/active'); setEmployees(r.data); }
    catch (err) { console.error('employees:', err); setEmployees([]); }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role]);

  const getUnit = (id) => units.find(u => u.id === Number(id));
  const getEmployee = (id) => employees.find(e => e.id === Number(id));
  const getVehicle = (id) => vehicles.find(v => v.id === Number(id));

  const totalExpenses = couriers.reduce((s, c) => s + Number(c.van_rent||0) + Number(c.fuel||0) + Number(c.misc_expense||0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form,
        from_unit_id: form.from_unit_id ? Number(form.from_unit_id) : null,
        to_unit_id: form.to_unit_id ? Number(form.to_unit_id) : null,
        sent_by_employee_id: form.sent_by_employee_id ? Number(form.sent_by_employee_id) : null,
        vehicle_id: form.vehicle_id ? Number(form.vehicle_id) : null,
        van_rent: Number(form.van_rent), fuel: Number(form.fuel), misc_expense: Number(form.misc_expense)
      };
      if (editing) await api.put(`/api/internal-couriers/${editing}`, payload);
      else await api.post('/api/internal-couriers', payload);
      setShowForm(false); setForm(EMPTY); setEditing(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleEdit = (c) => {
    setForm({ dispatch_date:c.dispatch_date||TODAY, from_unit_id:c.from_unit_id||'',
      to_unit_id:c.to_unit_id||'', sent_by_employee_id:c.sent_by_employee_id||'',
      handler_name:c.handler_name||'', vehicle_id:c.vehicle_id||'',
      expense_type:c.expense_type||'Van', van_rent:c.van_rent||0, fuel:c.fuel||0,
      misc_expense:c.misc_expense||0, remarks:c.remarks||'' });
    setEditing(c.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this courier?')) return;
    try { await api.delete(`/api/internal-couriers/${id}`); load(); }
    catch { alert('Delete failed'); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Internal Couriers</h1>
          <p className="text-gray-500 text-sm">{couriers.length} records</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}
          className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
          ➕ New Dispatch
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">{editing ? 'Edit Dispatch' : 'New Internal Dispatch'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dispatch Date *</label>
                <input type="date" value={form.dispatch_date} onChange={e => setForm({...form,dispatch_date:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expense Type</label>
                <select value={form.expense_type} onChange={e => setForm({...form,expense_type:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {EXPENSE_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Unit *</label>
                <select value={form.from_unit_id} onChange={e => setForm({...form,from_unit_id:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select Unit —</option>
                  {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Unit *</label>
                <select value={form.to_unit_id} onChange={e => setForm({...form,to_unit_id:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select Unit —</option>
                  {units.filter(u => String(u.id) !== String(form.from_unit_id)).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sent By Employee</label>
                <select value={form.sent_by_employee_id} onChange={e => setForm({...form,sent_by_employee_id:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— None —</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Handler Name</label>
                <input type="text" value={form.handler_name} onChange={e => setForm({...form,handler_name:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle (if applicable)</label>
                <select value={form.vehicle_id} onChange={e => setForm({...form,vehicle_id:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— None —</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Van Rent (Rs)</label>
                <input type="number" value={form.van_rent} onChange={e => setForm({...form,van_rent:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fuel (Rs)</label>
                <input type="number" value={form.fuel} onChange={e => setForm({...form,fuel:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Misc Expense (Rs)</label>
                <input type="number" value={form.misc_expense} onChange={e => setForm({...form,misc_expense:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea value={form.remarks} onChange={e => setForm({...form,remarks:e.target.value})} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Add Dispatch'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400"><div className="text-4xl animate-pulse mb-2">📫</div><p>Loading...</p></div>
        ) : couriers.length === 0 ? (
          <div className="p-12 text-center text-gray-400"><div className="text-5xl mb-3">📫</div><p>No internal courier records</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Date','From → To','Sent By','Handler','Vehicle','Type','Total Expense','Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {couriers.map(c => {
                  const totalExp = Number(c.van_rent||0) + Number(c.fuel||0) + Number(c.misc_expense||0);
                  return (
                    <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-600">{c.dispatch_date}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-800">{getUnit(c.from_unit_id)?.name||'—'}</div>
                        <div className="text-gray-400">→ {getUnit(c.to_unit_id)?.name||'—'}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{c.sent_by_employee_id ? getEmployee(c.sent_by_employee_id)?.name||'—' : '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{c.handler_name||'—'}</td>
                      <td className="px-4 py-3 text-gray-600">{c.vehicle_id ? getVehicle(c.vehicle_id)?.vehicle_number||'—' : '—'}</td>
                      <td className="px-4 py-3"><span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">{c.expense_type}</span></td>
                      <td className="px-4 py-3 font-medium text-gray-800">Rs {totalExp.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => handleEdit(c)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">✏️ Edit</button>
                          <button onClick={() => handleDelete(c.id)} className="text-red-500 hover:text-red-700 text-xs font-medium">🗑️</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-right">
              <span className="text-sm font-medium text-gray-700">Total Expenses: </span>
              <span className="text-sm font-bold text-gray-900">Rs {totalExpenses.toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
